// src/components/tiptap/toolItems.ts
import * as FiIcons from 'react-icons/fi';

const {
  FiPaperclip,
  FiPlus,
  FiMic,
  FiTarget,
  FiVideo,
  FiHeadphones,
  FiGrid,
  FiClock,
  FiShuffle,
  FiZap,
} = FiIcons;

type EditorLike = any;

type BuildOpts = {
  /** When true, delete the "/" trigger range before insert */
  deleteRange?: boolean;
  /** Range to delete (from SlashMenu) */
  range?: { from: number; to: number } | null;
};

type Item = {
  title: string;
  description: string;
  icon: any;
  command: () => void;
  locked?: boolean;
  lockedReason?: string;
};

function uuid() {
  // Browser-safe fallback
  // @ts-ignore
  return (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : String(Date.now());
}

export function buildToolItems(editor: EditorLike, opts: BuildOpts = {}): Item[] {
  if (!editor) return [];
  const { deleteRange = false, range = null } = opts;

  const fm = (editor?.storage?.mms?.featureMap && typeof editor.storage.mms.featureMap === 'object')
    ? editor.storage.mms.featureMap
    : {};
  const entitlementsLoading = !!editor?.storage?.mms?.entitlementsLoading;

  // SAFETY: preserve existing behavior if the entitlement row doesn't exist yet.
  // Only lock when tools_advanced is explicitly false.
  const hasToolsAdvanced = Object.prototype.hasOwnProperty.call(fm, 'tools_advanced')
    ? !!fm.tools_advanced
    : true;

  // Free plan allowance per pricing: only these two tools always allowed.
  const alwaysAllowed = new Set(['metronome', 'practiceTracker']);

  const gate = (key: string, item: Omit<Item, 'command'> & { command: () => void }): Item => {
    if (alwaysAllowed.has(key)) return item as Item;
    if (hasToolsAdvanced) return item as Item;

    const msg = entitlementsLoading ? 'Checking your plan…' : 'Upgrade to unlock all tools.';
    return {
      ...(item as Item),
      locked: true,
      lockedReason: msg,
      command: () => {
        if (entitlementsLoading) return;
        window.dispatchEvent(new CustomEvent('mms:upgradeRequired', {
          detail: { message: 'This tool is not available on your current plan. Upgrade to unlock all tools.' },
          bubbles: true,
          composed: true,
        }));
      },
    };
  };

  const withChain = () => {
    const chain = editor.chain().focus();
    if (deleteRange && range) chain.deleteRange(range);
    return chain;
  };

  // Helper: pick first node name that exists in the schema (keeps prod safe across renames)
  const resolveNode = (...candidates: string[]) => {
    const nodes = editor?.schema?.nodes ?? {};
    for (const name of candidates) {
      if (nodes[name]) return name;
    }
    return null;
  };

  const insertNodeWithParagraph = (name: string, attrs = {}) => {
    // Generate a fresh noteBlockId if not provided (idempotency)
    const finalAttrs = {
      noteBlockId: uuid(),
      ...attrs,
    };

    withChain()
      .insertContent([
        { type: name, attrs: finalAttrs },
        { type: 'paragraph' },
      ])
      .run();
  };

  return [
    gate('attachment', {
      title: 'Attachment',
      description: 'Upload a file or image inline',
      icon: FiPaperclip,
      command: () => {
        const nbid = uuid();
        withChain()
          .insertContent([
            { type: 'attachment', attrs: { noteBlockId: nbid } },
            { type: 'paragraph' },
          ])
          .run();
      },
    }),
    gate('metronome', {
      title: 'Metronome',
      description: 'Tap tempo and count beats inline',
      icon: FiPlus,
      command: () => {
        // Support historical + current schema names
        const name = resolveNode('metronome', 'metronomeTool');
        if (!name) {
          console.warn('[MMS] Metronome node not registered as "metronome" or "metronomeTool". Available nodes:', Object.keys(editor?.schema?.nodes ?? {}));
          return;
        }
        withChain()
          .insertContent([{ type: name }, { type: 'paragraph' }])
          .run();
      },
    }),
    gate('audioRecorder', {
      title: 'Record audio',
      description: 'Insert an inline audio recorder',
      icon: FiMic,
      command: () => {
        const nbid = uuid();
        withChain()
          .insertContent([
            { type: 'audioRecorder', attrs: { noteBlockId: nbid } },
            { type: 'paragraph' },
          ])
          .run();
      },
    }),
    gate('practiceTracker', {
      title: 'Practice tracker',
      description: 'Track practice sessions inline',
      icon: FiTarget,
      command: () => {
        const nbid = uuid();
        withChain()
          .insertContent([
            { type: 'practiceTracker', attrs: { noteBlockId: nbid } },
            { type: 'paragraph' },
          ])
          .run();
      },
    }),
    gate('youtubeLink', {
      title: 'YouTube Video',
      description: 'Embed a YouTube link inline',
      icon: FiVideo,
      command: () => {
        const nbid = uuid();
        withChain()
          .insertContent([
            { type: 'youtubeLink', attrs: { noteBlockId: nbid } },
            { type: 'paragraph' },
          ])
          .run();
      },
    }),
    gate('whatDoYouHear', {
      title: 'What Do You Hear?',
      description: 'Aural skills: listen and choose the answer',
      icon: FiHeadphones,
      command: () => {
        const nbid = uuid();
        withChain()
          .insertContent([
            { type: 'whatDoYouHear', attrs: { noteBlockId: nbid } },
            { type: 'paragraph' },
          ])
          .run();
      },
    }),
    gate('memoryMatch', {
      title: 'Memory Match',
      description: 'Flip cards and find matching pairs',
      icon: FiGrid,
      command: () => {
        const nbid = uuid();
        withChain()
          .insertContent([
            { type: 'memoryMatch', attrs: { noteBlockId: nbid } },
            { type: 'paragraph' },
          ])
          .run();
      },
    }),
    gate('timerTool', {
      title: 'Timer',
      description: 'Count up or run a countdown',
      icon: FiClock,
      command: () => {
        const nbid = uuid();
        withChain()
          .insertContent([
            { type: 'timerTool', attrs: { noteBlockId: nbid } },
            { type: 'paragraph' },
          ])
          .run();
      },
    }),
    gate('randomisePracticeDice', {
      title: 'Randomise my practice',
      description: 'Roll 1 or 2 dice with sound + animation',
      icon: FiShuffle,
      command: () => {
        const nbid = uuid();
        withChain()
          .insertContent([
            { type: 'randomisePracticeDice', attrs: { noteBlockId: nbid } },
            { type: 'paragraph' },
          ])
          .run();
      },
    }),
    gate('noteReadingGame', {
      title: 'Note Reading Game',
      description: 'Identify notes on the staff – inline',
      icon: FiHeadphones,
      command: () => {
        const nbid = uuid();
        withChain()
          .insertContent([
            { type: 'noteReadingGame', attrs: { noteBlockId: nbid } },
            { type: 'paragraph' },
          ])
          .run();
      },
    }),
    gate('practiceBingo', {
      title: 'Practice Bingo',
      description: 'Generate a bingo card and tick off squares',
      icon: FiGrid,
      command: () => {
        const nbid = uuid();
        const m = editor.storage.mms || {};
        withChain()
          .insertContent([
            { type: 'practiceBingo', attrs: { noteBlockId: nbid, schoolId: m.schoolId } },
            { type: 'paragraph' },
          ])
          .run();
      },
    }),
    gate('interactiveVideo', {
      title: 'Interactive Video',
      description: 'Request a video and leave timestamped feedback',
      icon: FiVideo,
      command: () => {
        const nbid = uuid();
        withChain()
          .insertContent([
            { type: 'interactiveVideo', attrs: { noteBlockId: nbid } },
            { type: 'paragraph' },
          ])
          .run();
      },
    }),
    // ─────────────────────────────────────────────
    // NEW: Performance Interruption Generator (PIG)
    gate('performanceInterruption', {
      title: 'Performance Interruption Generator',
      description: 'Random interruptions while the student plays',
      icon: FiZap,
      command: () => {
        const nbid = uuid();
        const m = editor.storage.mms || {};
        withChain()
          .insertContent([
            {
              type: 'performanceInterruption',
              attrs: {
                noteBlockId: nbid,
                schoolId: m.schoolId,
                userId: m.userId,
              },
            },
            { type: 'paragraph' },
          ])
          .run();
      },
    }),
  ];
}